import React from 'react';
import {
  Building2Icon,
  CarFrontIcon,
  LandmarkIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';

const ease = [0.22, 1, 0.36, 1] as const;
const driverShift = '/photo_2026-07-12_14-08-25_(2).jpg';
const boltTrip = '/photo_2026-07-06_02-43-07.jpg';

type StakeholderId = 'driver' | 'company' | 'authority';

type Stakeholder = {
  id: StakeholderId;
  step: string;
  eyebrow: string;
  title: string;
  copy: string;
  points: string[];
  icon: typeof CarFrontIcon;
  visual: React.ReactNode;
  surface: string;
  border: string;
  accent: string;
  mutedAccent: string;
};

const stakeholders: Stakeholder[] = [
  {
    id: 'driver',
    step: '01',
    eyebrow: 'Förarappen · för föraren',
    title: 'Håll koll på din dag.',
    copy: 'Dina taxiappar, körpass, raster, vilotid och ekonomi — samlat på ett ställe. Ditt digitala ID finns alltid nära.',
    points: [
      'Alla taxiappar på ett ställe',
      'Körpass, vilotid och rast i realtid',
      'Ekonomi och digitalt ID',
    ],
    icon: CarFrontIcon,
    visual: <DriverVisual />,
    surface: '#102C3F',
    border: 'rgba(34, 211, 238, 0.4)',
    accent: '#67E8F9',
    mutedAccent: '#06B6D4',
  },
  {
    id: 'company',
    step: '02',
    eyebrow: 'Företagsvy · för taxiföretaget',
    title: 'Se hela företaget. Agera i tid.',
    copy: 'Förare, fordon, resor, inkomster och regler i en gemensam vy. Se vad som behöver åtgärdas innan det blir ett problem.',
    points: [
      'Förare, fordon och resor samlat',
      'Inkomster med en tydlig bild',
      'Löpande koll på vilotiden',
    ],
    icon: Building2Icon,
    visual: <DashboardVisual />,
    surface: '#0D3150',
    border: 'rgba(56, 189, 248, 0.42)',
    accent: '#7DD3FC',
    mutedAccent: '#38BDF8',
  },
  {
    id: 'authority',
    step: '03',
    eyebrow: 'P-ivilotid · för myndigheten',
    title: 'Kontroll med rätt underlag.',
    copy: 'Sök på registreringsnummer och se vilotidsrapport, tidbok och tillstånd. Vyn är skrivskyddad — inget kan ändras.',
    points: [
      'Sök på registreringsnummer',
      'Skrivskyddad kontrollvy',
      'Förar-ID och underlag separat',
    ],
    icon: LandmarkIcon,
    visual: <AuthorityVisual />,
    surface: '#292817',
    border: 'rgba(245, 196, 0, 0.5)',
    accent: '#FBEA87',
    mutedAccent: '#F5C400',
  },
];

export function StakeholderExperience() {
  return (
    <section id="tre-ytor" className="bg-[#0B1120] py-14 text-white sm:py-16">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[.12em] text-[#A5F3FC]">
              En plattform. Tre roller.
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.08] tracking-[-.04em] sm:text-4xl">
              Gjord för föraren, företaget och myndigheten.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-6 text-slate-300">
            Tre vyer som hänger ihop — med samma tillförlitliga data.
          </p>
        </div>
        <div className="mt-5 grid gap-3 lg:grid-cols-3" aria-label="iVilotids tre ytor">
          {stakeholders.map((item, index) => (
            <StakeholderCard key={item.id} item={item} index={index} />
          ))}
        </div>
        <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-6 text-slate-200">
          <span className="h-1.5 w-1.5 rounded-full bg-[#67E8F9]" aria-hidden="true" />
          Förare<span className="text-slate-400">·</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8]" aria-hidden="true" />
          Taxiföretag<span className="text-slate-400">·</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#F5C400]" aria-hidden="true" />
          Myndighet<span className="text-slate-400">—</span>samma data, tre vyer.
        </p>
      </div>
    </section>
  );
}

function StakeholderCard({ item, index }: { item: Stakeholder; index: number }) {
  const Icon = item.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.28, delay: index * 0.05, ease }}
      style={{ backgroundColor: item.surface, borderColor: item.border }}
      className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border p-4 sm:p-5"
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ backgroundColor: item.mutedAccent }}
      />
      <div className="flex items-center justify-between gap-3">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#071A28]"
          style={{ backgroundColor: item.mutedAccent }}
        >
          <Icon size={18} aria-hidden="true" />
        </span>
        <span
          className="font-display text-xl tracking-[-.04em]"
          style={{ color: item.accent }}
        >
          {item.step}
        </span>
      </div>
      <p
        className="mt-4 text-xs font-bold uppercase tracking-[.08em]"
        style={{ color: item.accent }}
      >
        {item.eyebrow}
      </p>
      <h3 className="mt-1.5 font-display text-xl leading-[1.12] tracking-[-.03em] text-white">
        {item.title}
      </h3>
      <p className="mt-2.5 text-sm leading-6 text-slate-100">{item.copy}</p>
      <div className="mt-4 flex min-h-[142px] items-center justify-center">{item.visual}</div>
      <ul className="mt-4 space-y-2 border-t border-white/10 pt-3.5">
        {item.points.map((point) => (
          <li key={point} className="flex gap-2 text-sm leading-5 text-white">
            <span
              className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: item.mutedAccent }}
              aria-hidden="true"
            />
            {point}
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function DriverVisual() {
  return (
    <div className="relative aspect-[4/5] w-full max-w-[115px]" aria-label="Exempel på förarapp">
      <div className="absolute bottom-0 left-0 h-[92%] w-[78%] overflow-hidden rounded-xl border-[3px] border-[#071A28] bg-white shadow-[0_8px_18px_rgba(0,0,0,.24)]">
        <img
          src={driverShift}
          alt="iVilotid mobilvy för körpass och vilotid"
          loading="lazy"
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="absolute right-0 top-[4%] h-[37%] w-[47%] overflow-hidden rounded-lg border-[2px] border-[#071A28] bg-white shadow-[0_6px_14px_rgba(0,0,0,.24)]">
        <img
          src={boltTrip}
          alt="Anslutet Bolt-uppdrag i iVilotid"
          loading="lazy"
          className="h-full w-full object-cover object-top"
        />
      </div>
    </div>
  );
}

function DashboardVisual() {
  return (
    <div className="w-full max-w-[170px] rounded-xl border border-white/15 bg-[#071A28] p-3 shadow-[0_8px_18px_rgba(0,0,0,.24)]">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[.08em] text-[#BAE6FD]">Företagsvy</p>
          <p className="mt-0.5 text-sm font-semibold text-white">Aktivt läge</p>
        </div>
        <span className="h-2 w-2 rounded-full bg-[#38BDF8]" aria-hidden="true" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {['Förare', 'Fordon', 'Resor', 'Regler'].map((item, index) => (
          <div key={item} className="min-w-0 rounded-lg border border-white/10 bg-white/[0.06] p-2">
            <div className={`h-1 w-6 rounded-full ${index === 3 ? 'bg-[#F5C400]' : 'bg-[#38BDF8]'}`} />
            <p className="mt-2 truncate text-xs font-semibold text-white">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuthorityVisual() {
  return (
    <div className="w-full max-w-[170px] rounded-xl border border-[#F5C400]/45 bg-[#0B1120] p-3 shadow-[0_8px_18px_rgba(0,0,0,.24)]">
      <div className="flex items-center justify-between gap-2">
        <ShieldCheckIcon className="text-[#F5C400]" size={19} aria-hidden="true" />
        <span className="rounded-md border border-[#F5C400]/50 px-1.5 py-0.5 text-xs font-bold uppercase tracking-[.05em] text-[#FEF3A7]">
          Skrivskyddad
        </span>
      </div>
      <p className="mt-4 text-xs font-bold uppercase tracking-[.08em] text-[#A5F3FC]">P-ivilotid</p>
      <p className="mt-1 text-sm font-semibold text-white">Kontroll vid vägkanten</p>
      <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.06] p-2.5">
        <p className="text-xs font-bold uppercase tracking-[.06em] text-slate-300">
          Reg
        </p>
        <p className="mt-1.5 rounded-md bg-white px-2.5 py-2 text-sm font-bold tracking-[.08em] text-[#0B1120]">
          ABC 123
        </p>
      </div>
    </div>
  );
}
